import { Flex, Table, TableBody, TableCell, TableHead, TableRow } from '@aws-amplify/ui-react';
import type { Edulead } from 'edulead';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { TbInfoCircle } from 'react-icons/tb';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CreateExportBar } from '../components/hubspot-companies/CreateExportBar';
import { MatchRunProgress } from '../components/hubspot-companies/MatchRunProgress';
import Banner from '../custom-components/Banner';
import LoadingBars from '../custom-components/LoadingBars';
import NA from '../custom-components/NA';
import NothingHere from '../custom-components/NothingHere';
import { SegmentField } from '../custom-components/SegmentField';
import { TimestampDisplay } from '../custom-components/TimestampDisplay';
import UniversalTag from '../custom-components/UniversalTag';
import { GlobalContext } from '../global-context';
import { useMatchRun } from '../hooks/useMatchRun';
import { useMemberEmails } from '../hooks/useMemberEmails';
import { enumLabel, toastV4Error } from '../services/edulead-v4';
import { eduleadV4 } from '../services/edulead-v4-client';
import General from '../utils/GeneralUtils';
import './MatchRun.scss';

/** The grid is a sample for eyeballing quality, not a browser - the counts above it
 *  and the export itself come from the server, not from what is rendered here. */
const ROWS_SHOWN = 50;

/** Matches the boss detail tables: fixed-width label column, top-aligned. */
const LABEL_CELL = { width: '180px', verticalAlign: 'top' as const };

const FAILURE_LABELS: Record<string, string> = {
  NO_CANDIDATE: 'No candidate',
  BELOW_THRESHOLD: 'Below threshold',
  MULTIPLE: 'Multiple candidates',
  NO_NAME: 'Empty name property',
  NO_STATE: 'No state mapped',
};

/** The row's failure reason as FAILURE_LABELS keys it (NO_CANDIDATE, …); '' when none. */
function failureReason(row: Edulead.EduleadV4MatchRow): string {
  return enumLabel(row.failure_reason, 'MATCH_FAILURE_REASON_');
}

/** A column header with an explanation on hover, using the app's shared tooltip. */
function ColumnHeader({ label, hint }: { label: string; hint: string }) {
  return (
    <TableCell as="th">
      <Flex as="span" alignItems="center" gap={4}>
        {label}
        <TbInfoCircle
          size={14}
          style={{ cursor: 'help', flexShrink: 0 }}
          data-tooltip-id="main-tooltip"
          data-tooltip-content={hint}
        />
      </Flex>
    </TableCell>
  );
}

/** One label/value row, matching the boss detail tables. */
function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell as="th" style={LABEL_CELL}>{label}</TableCell>
      <TableCell>{children}</TableCell>
    </TableRow>
  );
}

function DetailTable({ children }: { children: React.ReactNode }) {
  return (
    <Table backgroundColor="white" size="small" variation="bordered" flex="1">
      <TableBody>{children}</TableBody>
    </Table>
  );
}

export function MatchRunDetail() {
  const globalContext = useContext(GlobalContext);
  const navigate = useNavigate();
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  General.setTitle(document, 'HubSpot Company Enrichment', globalContext);

  const { run, status, stats, exportLimit, progress, isActive, isLoading } = useMatchRun(id);
  const memberEmails = useMemberEmails();

  const activeTab = tab === 'unmatched' ? 'unmatched' : 'matched';
  const [rows, setRows] = useState<Edulead.EduleadV4MatchRow[]>([]);
  const [isLoadingRows, setIsLoadingRows] = useState(false);
  const [isCreatingExport, setIsCreatingExport] = useState(false);

  const isReady = status === 'READY';
  // The tab's total comes from the run's stats, as the API pages rows without a count
  const rowsTotal = (activeTab === 'matched' ? stats?.matched : stats?.unmatched) ?? 0;
  const hsPortalId = run?.hubspot_portal_id;

  const loadRows = useCallback(async () => {
    if (!id || !isReady) return;

    setIsLoadingRows(true);
    try {
      const page = await eduleadV4.matches.listRows({
        match_id: id,
        'filter.outcome': activeTab === 'matched' ? 'MATCH_OUTCOME_MATCHED' : 'MATCH_OUTCOME_NO_MATCH',
        page_size: ROWS_SHOWN,
        order_by: activeTab === 'matched' ? 'match_confidence desc' : 'company_name',
      });
      setRows(page.data);
    } catch (error) {
      toastV4Error(error, 'Failed to load match run rows');
    } finally {
      setIsLoadingRows(false);
    }
  }, [id, isReady, activeTab]);

  useEffect(() => { loadRows(); }, [loadRows]);

  const handleCreateExport = async () => {
    if (!id) return;

    setIsCreatingExport(true);
    try {
      const { export: created } = await eduleadV4.exports.create({
        request_id: crypto.randomUUID(),
        export: { match_id: id },
      });
      if (created?.id) {
        navigate(`/exports/${created.id}/fields`);
      }
    } catch (error) {
      toastV4Error(error, 'Failed to create the export');
    } finally {
      setIsCreatingExport(false);
    }
  };

  if (isLoading) {
    return <div className="content content-1000"><LoadingBars numberOfRows={8} /></div>;
  }

  if (!run) {
    return <div className="content content-1000"><NothingHere text='Match run not found' /></div>;
  }

  return (
    <div className="content content-1000">
      <h3 className="match-run__title match-run__title--standalone">
        <Link to='/tools/hubspot-companies'>HubSpot Company Enrichment</Link>
        {' / '}
        {(run.id ?? '').split('-')[0]}
      </h3>

      {/* Held back while the run works: the counts are still filling in, and the progress
          below already says what it is doing. Split in two so the run itself reads
          separately from what it produced. */}
      {!isActive && (
        <Flex gap={20} alignItems='flex-start' marginBottom='20px'>
          <DetailTable>
            <DetailRow label='Status'><UniversalTag category={status} /></DetailRow>
            <DetailRow label='Started'><TimestampDisplay timestamp={run.create_time} /></DetailRow>
            <DetailRow label='Started by'>{memberEmails.get(run.creator_id ?? '') || <NA />}</DetailRow>
          </DetailTable>

          <DetailTable>
            <DetailRow label='Strategy'><UniversalTag category={enumLabel(run.strategy, 'MATCH_STRATEGY_')} /></DetailRow>
            <DetailRow label='HubSpot companies found'>
              {stats?.total ? stats.total.toLocaleString() : <NA />}
            </DetailRow>
            <DetailRow label='Matched to institutions'>
              {stats?.matched
                ? <>
                    {stats.matched.toLocaleString()}
                    {!!stats.total && ` (${Math.round((stats.matched / stats.total) * 100)}%)`}
                  </>
                : <NA />}
            </DetailRow>
          </DetailTable>
        </Flex>
      )}

      {isActive && (
        <MatchRunProgress
          status={status}
          progress={progress}
          nrCompaniesFetched={run.companies_fetched}
          nrCompaniesTotal={run.companies_total}
          nrCompaniesMatched={run.companies_matched}
        />
      )}

      {/* One line: .banner__content lays its children out in a row, so stacked divs come
          out side by side. */}
      {status === 'FAILED' && (
        <Banner color='orange'>
          This match run stopped before it finished.{run.failure_message ? ` ${run.failure_message}` : ''}
        </Banner>
      )}

      {isReady && stats && (
        <>
          <h3>Match preview</h3>

          <SegmentField
            mode='single'
            options={[
              { id: 'matched', label: `Matched (${(stats.matched ?? 0).toLocaleString()})` },
              { id: 'unmatched', label: `Unmatched (${(stats.unmatched ?? 0).toLocaleString()})` },
            ]}
            selectedId={activeTab}
            onChange={(value: string) => navigate(`/tools/hubspot-companies/${id}/${value}`)}
            className="match-run__tabs"
          />

          {isLoadingRows ? (
            <LoadingBars numberOfRows={10} />
          ) : rows.length === 0 ? (
            <NothingHere text={activeTab === 'matched' ? 'No matches found' : 'No unmatched companies found'} />
          ) : (
            <Table size='small' highlightOnHover={true}>
              <TableHead>
                <TableRow>
                  <ColumnHeader
                    label='HubSpot company'
                    hint='A company from your HubSpot account, with the address and state we read from it. Opens in HubSpot.'
                  />
                  {activeTab === 'matched' ? (
                    <>
                      <ColumnHeader
                        label='Edulead matched institution'
                        hint='The institution in our database this company was matched to, with its address for comparison.'
                      />
                      <TableCell as="th">Type</TableCell>
                      <ColumnHeader
                        label='Confidence'
                        hint='100% is an exact match — the same NCES ID, or the same name once abbreviations are expanded. Lower scores mean the names differ.'
                      />
                    </>
                  ) : (
                    <>
                      <ColumnHeader
                        label='Value we matched on'
                        hint='The value read from the HubSpot property you mapped. If this column is empty or holds the wrong thing, the property mapping is wrong.'
                      />
                      <ColumnHeader
                        label='Reason'
                        hint='Why this company could not be matched.'
                      />
                      <ColumnHeader
                        label='Closest candidate'
                        hint='The nearest institution we considered and rejected, with its score. Useful for telling a near miss from nothing to match against.'
                      />
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      {hsPortalId ? (
                        <a
                          href={`https://app.hubspot.com/contacts/${hsPortalId}/company/${row.hubspot_company_id}`}
                          target='_blank'
                          rel='noreferrer'
                        >
                          {row.company_name || row.hubspot_company_id} <FaExternalLinkAlt size={9} />
                        </a>
                      ) : (row.company_name || row.hubspot_company_id)}
                      {(row.company_address || row.company_state) && (
                        <div className="desc-2">
                          {[row.company_address, row.company_state].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </TableCell>

                    {activeTab === 'matched' ? (
                      <>
                        <TableCell>
                          <Link to={`/directory/institutions/${row.institution_id}/contacts`}>
                            {row.institution_name}
                          </Link>
                          {row.tier === 'MATCH_TIER_NCES_ALT' && (
                            <> <UniversalTag category='NCES_ALT' /></>
                          )}
                          {/* The point of this grid is verifying a match, which needs both
                              sides shown the same way. */}
                          {row.institution_address && (
                            <div className="desc-2">{row.institution_address}</div>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.institution_type ? <UniversalTag category={row.institution_type} /> : <NA />}
                        </TableCell>
                        <TableCell>
                          {row.match_confidence != null ? `${Math.floor(row.match_confidence * 100)}%` : <NA />}
                        </TableCell>
                      </>
                    ) : (
                      <>
                        {/* Echoing the value read from their mapped property is what makes a
                            wrong field mapping obvious at a glance. */}
                        <TableCell>
                          <span className="desc-2">{row.company_name || <NA />}</span>
                        </TableCell>
                        <TableCell>
                          {failureReason(row)
                            ? <UniversalTag category={FAILURE_LABELS[failureReason(row)] ?? failureReason(row)} />
                            : <NA />}
                        </TableCell>
                        <TableCell>
                          {row.best_candidate_name ? (
                            <>
                              {row.best_candidate_name}
                              {row.best_candidate_score != null && (
                                <span className="desc-2"> ({Math.floor(row.best_candidate_score * 100)}%)</span>
                              )}
                            </>
                          ) : <NA />}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {rowsTotal > rows.length && (
            <div className="desc-2" style={{ marginTop: 8 }}>
              Showing {rows.length.toLocaleString()} of {rowsTotal.toLocaleString()} — a sample
              for checking match quality. The export takes every match.
            </div>
          )}

          {activeTab === 'matched' && (
            <CreateExportBar
              institutionsIncluded={stats.distinct_institutions ?? 0}
              exportLimit={exportLimit}
              isCreating={isCreatingExport}
              onCreateExport={handleCreateExport}
            />
          )}
        </>
      )}
    </div>
  );
}
